import React from 'react'
import { connect } from 'react-redux'

import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContentText from '@material-ui/core/DialogContentText'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import { FormattedMessage } from 'react-intl'
import DialogActions from '@material-ui/core/DialogActions'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import MultipleStudentAddComponent from './MultipleStudentAddComponent'
import { organizationActions } from '../../_state/actions'

const resultStyles = {
    resultSummary: {
        backgroundColor: '#f5f5f5',
        borderRadius: 8,
        padding: 16,
        marginTop: 16,
    },
    resultRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
    },
    errorTable: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: 12,
    },
    errorTableHeader: {
        backgroundColor: '#f5f5f5',
        textAlign: 'left',
        padding: '8px 12px',
        fontWeight: 600,
        fontSize: 13,
    },
    errorTableCell: {
        padding: '6px 12px',
        borderBottom: '1px solid #eee',
        fontSize: 13,
    },
}

const MultipleStudentAddModal = ({
    inviteEmailsOpen,
    onInviteEmailsClose,
    title,
    description,
    role = 'Student',
    onInviteStudentsToSchool,
    organization,
    dispatch,
}) => {
    const [addStudentRows, setAddStudentRows] = React.useState([])
    const [hasAnyValidEmails, setHasAnyValidEmails] = React.useState(false)

    const addingStudents = organization.addingStudents
    const inviteResult = organization.studentInviteResult

    const handleSendInviteStudentClick = () => {
        onInviteStudentsToSchool(addStudentRows, role)
    }

    const handleStudentRowsAreUpdated = (studentRows) => {
        setAddStudentRows(studentRows)
        setHasAnyValidEmails(studentRows.filter((x) => x.validEmail).length > 0)
    }

    const handleClose = () => {
        dispatch(organizationActions.resetStudentInvite())
        onInviteEmailsClose()
    }

    return (
        <Dialog
            open={inviteEmailsOpen}
            onClose={handleClose}
            maxWidth={'lg'}
            aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">
                {inviteResult ? 'Invite Complete' : title}
            </DialogTitle>
            <DialogContent>
                {!inviteResult && !addingStudents && (
                    <>
                        <DialogContentText>{description}</DialogContentText>
                        <MultipleStudentAddComponent
                            onStudentRowsChanged={handleStudentRowsAreUpdated}
                            onInviteStudentsToSchool={onInviteStudentsToSchool}
                        />
                    </>
                )}

                {addingStudents && (
                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                            Inviting users, please wait...
                        </Typography>
                    </div>
                )}

                {inviteResult && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                            <CheckCircleIcon style={{ color: '#4caf50' }} />
                            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                                All users have been processed
                            </Typography>
                        </div>

                        <div style={resultStyles.resultSummary}>
                            <div style={resultStyles.resultRow}>
                                <Typography variant="body2">Total users processed</Typography>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    {inviteResult.totalUsers}
                                </Typography>
                            </div>
                            <div style={resultStyles.resultRow}>
                                <Typography variant="body2">New users invited</Typography>
                                <Typography variant="body2" style={{ fontWeight: 600, color: '#4caf50' }}>
                                    {inviteResult.usersInvited}
                                </Typography>
                            </div>
                            <div style={resultStyles.resultRow}>
                                <Typography variant="body2">Users already in organization</Typography>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    {inviteResult.usersAlreadyInOrganization}
                                </Typography>
                            </div>
                        </div>

                        {inviteResult.errors && inviteResult.errors.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                                <Typography variant="body2" style={{ fontWeight: 600, color: '#f44336', marginBottom: 8 }}>
                                    <ErrorIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                                    {inviteResult.errors.length} user(s) had issues:
                                </Typography>
                                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    <table style={resultStyles.errorTable}>
                                        <thead>
                                            <tr>
                                                <th style={resultStyles.errorTableHeader}>Email</th>
                                                <th style={resultStyles.errorTableHeader}>Error</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {inviteResult.errors.map((err, idx) => (
                                                <tr key={idx}>
                                                    <td style={resultStyles.errorTableCell}>{err.email}</td>
                                                    <td style={resultStyles.errorTableCell}>{err.errorMessage}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>

            <DialogActions>
                {inviteResult ? (
                    <Button onClick={handleClose} color="primary">
                        <FormattedMessage id="globalwords.close" defaultMessage="Close" />
                    </Button>
                ) : (
                    <>
                        <Button onClick={handleClose} color="primary" disabled={addingStudents}>
                            <FormattedMessage id="globalwords.cancel" defaultMessage="Cancel" />
                        </Button>
                        <Button
                            onClick={handleSendInviteStudentClick}
                            variant={'contained'}
                            disabled={!hasAnyValidEmails || addingStudents}
                            color="primary">
                            {addingStudents ? (
                                <CircularProgress size={18} color="inherit" style={{ marginRight: 8 }} />
                            ) : null}
                            <FormattedMessage id="globalwords.invite" defaultMessage="Invite" />
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    )
}

MultipleStudentAddModal.propTypes = {}

const mapStateToProps = (state) => {
    return {
        organization: state.organization,
    }
}

export default connect(mapStateToProps)(MultipleStudentAddModal)
